import { Button } from "@decibeltrade/web-core/components";
import { ModalContent, ModalRenderProps, openModal } from "@decibeltrade/web-core/modal";
import { sendGAEvent } from "@next/third-parties/google";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { css } from "styled-system/css";
import { stack } from "styled-system/patterns";

import { useAnalytics } from "@/contexts/analytics-context";
import { useWriteDexSdk } from "@/contexts/write-dex-sdk-context";
import { useDecibelWallet } from "@/hooks/use-decibel-wallet";
import { useTransactionErrorToast } from "@/hooks/use-tx-error-toast";
import { BaseEventProps } from "@/lib/analytics";
import { Logger } from "@/utils/logger";

import { toast } from "../toast";
import { ModalCloseButton } from "./modal-close-button";

export function openDelegationModal() {
  openModal({
    id: "delegation-modal",
    renderContent: (modalProps) => <DelegationModal {...modalProps} />,
  });
}

function DelegationModal({ close }: ModalRenderProps) {
  const t = useTranslations("walletSelector");

  const { handleDelegate, isDelegateLoading } = useHandleDelegate({ onSuccess: close });

  return (
    <ModalContent className={stack({ w: "[359px]", p: "16", gap: "24" })}>
      <ModalCloseButton
        close={close}
        className={css({ top: "16", right: "16", "& svg": { color: "icon.tertiary" } })}
      />
      <div className={stack({ gap: "12", pt: "12", textAlign: "center" })}>
        <h2 className={css({ textStyle: "heading.3xs" })}>{t("establishConnection")}</h2>
        <p className={css({ textStyle: "body.md" })}>{t("establishConnectionDescription")}</p>
      </div>
      <Button onClick={handleDelegate} loading={isDelegateLoading}>
        {t("establishConnection")}
      </Button>
    </ModalContent>
  );
}

export function useHandleDelegate(args: { onSuccess?: () => void }) {
  const Analytics = useAnalytics();
  const t = useTranslations("walletSelector");
  const generalT = useTranslations("general");

  const { connected } = useDecibelWallet();
  const { delegateToSessionKey } = useWriteDexSdk();
  const showTransactionErrorToast = useTransactionErrorToast();
  const [isDelegateLoading, setIsDelegateLoading] = useState(false);

  const handleDelegate = async () => {
    if (!connected) return;
    setIsDelegateLoading(true);
    const eventProps: BaseEventProps = {
      status: "initiated",
    };
    try {
      await delegateToSessionKey();
      toast({
        variant: "success",
        title: generalT("success"),
        description: t("delegatedToSessionKey"),
      });
      sendGAEvent("event", "delegate_success");
      args.onSuccess?.();
      eventProps.status = "success";
    } catch (error) {
      eventProps.status = "error";
      eventProps.error = error;
      showTransactionErrorToast(error, t("failedToDelegateToSessionKey"));
      sendGAEvent("event", "delegate_error");
      Logger.error(error, {
        message: "Failed to delegate to session key",
      });
    } finally {
      Analytics.userBehavior.delegation(eventProps);
      setIsDelegateLoading(false);
    }
  };

  return { handleDelegate, isDelegateLoading };
}
